import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { FC } from "react";

import { Icon } from "../../../Icon/Icon";
import { alpha } from "../../../../typography";
import { ReactComponent as ChevronIcon } from "../../../../assets/icons/chevron-right.svg";
import { AllIconName, IconVariant } from "../../../Icon/types";

type Variant = "success" | "error" | "warning";

export enum MenuOptionTestIds {
  Component = "option",
}

type ContainerProps = {
  $isMenu?: boolean;
  $avoidHover?: boolean;
  $isFirst?: boolean;
  $isLast?: boolean;
  $variant?: Variant;
  $disabled?: boolean;
};

const Container = styled.div<ContainerProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  min-width: max-content;
  padding: 8px;
  user-select: none;
  ${({ $variant }) => {
    switch ($variant) {
      case "success":
        return alpha.success.body3;
      case "error":
        return alpha.error.body3;
      case "warning":
        return alpha.warning.body3;
      default:
        return alpha.primaryActive.body3;
    }
  }}
  ${({ $variant }): FlattenSimpleInterpolation => {
    switch ($variant) {
      case "success":
        return css`
          svg {
            --main-color: var(--color-success-800);
          }
        `;
      case "error":
        return css`
          svg {
            --main-color: var(--color-error-500);
          }
        `;
      case "warning":
        return css`
          svg {
            --main-color: var(--color-warning-800);
          }
        `;
      default:
        return css``;
    }
  }}
  ${({ $isMenu }): FlattenSimpleInterpolation =>
    $isMenu
      ? css``
      : css`
          cursor: pointer;
        `}
  ${({ $isFirst }): FlattenSimpleInterpolation =>
    $isFirst
      ? css`
          border-top-left-radius: var(--size-border-radius-small);
          border-top-right-radius: var(--size-border-radius-small);
        `
      : css``}
  ${({ $isLast }): FlattenSimpleInterpolation =>
    $isLast
      ? css`
          border-bottom-left-radius: var(--size-border-radius-small);
          border-bottom-right-radius: var(--size-border-radius-small);
        `
      : css``}
  ${({ $disabled }): FlattenSimpleInterpolation =>
    $disabled
      ? css`
          color: var(--color-primary-200);
          cursor: not-allowed;
          svg {
            --main-color: var(--color-primary-200);
          }
        `
      : css`
          &:hover {
            background-color: var(--color-secondary-200);
          }
        `}
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

type ChevronProps = {
  $visible: boolean;
};

const Chevron = styled(ChevronIcon)<ChevronProps>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  width: 16px;
  margin-left: 24px;
`;

type BasicOptionProps = {
  id?: string;
  className?: string;
  testId?: string;
  iconName?: AllIconName;
  isMenu?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  variant?: Variant;
  iconVariant?: IconVariant;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  preventDefault?: boolean;
};

export const MenuOption: FC<BasicOptionProps> = ({
  id,
  className,
  testId,
  iconName,
  isMenu = false,
  children,
  isFirst = false,
  isLast = false,
  variant,
  iconVariant = "DEFAULT",
  disabled,
  onClick,
  preventDefault,
}) => (
  <Container
    id={id}
    className={className}
    data-testid={testId ?? MenuOptionTestIds.Component}
    $isFirst={isFirst}
    $isLast={isLast}
    $variant={variant}
    $isMenu={isMenu}
    $disabled={disabled}
    onClick={disabled ? undefined : onClick}
    onMouseDown={(event) => preventDefault && event.preventDefault()}
  >
    <Label>
      {iconName && (
        <Icon
          iconName={iconName}
          iconSize="small"
          variant={iconVariant}
          plain
        />
      )}
      {children}
    </Label>
    <Chevron $visible={isMenu} />
  </Container>
);
